import { Close as CloseIcon } from "@mui/icons-material";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import { usePostHog } from "@posthog/react";
import React, { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSearchParams } from "react-router-dom";
import { SessionSlideModel } from "../../object-actions/types/types";
import { capture } from "../../analytics/posthog";
import YouTubePlayback from "./YouTubePlayback";
import YouTubeSearch, { QuerySource } from "./YouTubeSearch";
import { YouTubeVideo } from "./youtubeApi";

interface YouTubeModalProps {
    open: boolean;
    onClose: () => void;
    slide: SessionSlideModel;
}

const YouTubeModal: React.FC<YouTubeModalProps> = ({ open, onClose, slide }) => {
    const posthog = usePostHog();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialVideoId = searchParams.get("ytv");
    const [selectedVideo, setSelectedVideo] = useState<YouTubeVideo | null>(() =>
        initialVideoId
            ? {
                videoId: initialVideoId,
                title: "",
                channelTitle: "",
                thumbnail: "",
                publishedAt: "",
            }
            : null
    );

    // Dev backdoor: restore a prior user query / selected video from the URL on reload.
    const initialQuery = searchParams.get("ytq") ?? undefined;

    const urlVideoId = searchParams.get("ytv");

    useEffect(() => {
        if (!open || !urlVideoId) {
            return;
        }
        setSelectedVideo((prev) => {
            if (prev?.videoId === urlVideoId) {
                return prev;
            }
            return {
                videoId: urlVideoId,
                title: "",
                channelTitle: "",
                thumbnail: "",
                publishedAt: "",
            };
        });
    }, [open, urlVideoId]);

    const handleClose = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("yt");
                next.delete("ytq");
                next.delete("ytv");
                next.delete("yts");
                return next;
            },
            { replace: true }
        );
        onClose();
    }, [onClose, setSearchParams]);

    const handleSelectVideo = useCallback(
        (video: YouTubeVideo, querySource: QuerySource, options?: { userInitiated?: boolean }) => {
            setSelectedVideo(video);
            if (options?.userInitiated) {
                capture(posthog, "youtube_video_selected", {
                    slide_id: slide.id,
                    video_id: video.videoId,
                    query_source: querySource,
                });
            }
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("yt", "1");
                    next.set("yts", String(slide.id));
                    next.set("ytv", video.videoId);
                    return next;
                },
                { replace: true }
            );
        },
        [posthog, slide.id, setSearchParams]
    );

    const handleInitialLoadComplete = useCallback(
        (video: YouTubeVideo, querySource: QuerySource) => {
            if (urlVideoId) {
                return;
            }
            handleSelectVideo(video, querySource);
        },
        [handleSelectVideo, urlVideoId]
    );

    const handleQueryChange = useCallback(
        (query: string) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("yt", "1");
                    next.set("yts", String(slide.id));
                    if (query) {
                        next.set("ytq", query);
                    } else {
                        next.delete("ytq");
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [slide.id, setSearchParams]
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            data-testid="youtube-modal"
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 1 }}
            >
                <Box sx={{ pr: 2, minWidth: 0 }}>
                    {slide.title ? (
                        <Typography variant="h6" component="h2">
                            {slide.title}
                        </Typography>
                    ) : null}
                    {slide.script_open ? (
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ mt: slide.title ? 0.75 : 0, fontWeight: 500, lineHeight: 1.4 }}
                        >
                            {slide.script_open}
                        </Typography>
                    ) : null}
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    data-attr="youtube_close-modal"
                    sx={{ mt: -0.5 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 1 }}>
                {slide.body ? (
                    <Box
                        sx={{
                            mb: 1.5,
                            color: "text.secondary",
                            typography: "body2",
                            lineHeight: 1.6,
                            "& p": { margin: 0 },
                            "& p + p": { mt: 1 },
                        }}
                    >
                        <ReactMarkdown>{slide.body}</ReactMarkdown>
                    </Box>
                ) : null}

                <YouTubePlayback
                    videoId={selectedVideo?.videoId ?? null}
                    thumbnail={selectedVideo?.thumbnail}
                    title={selectedVideo?.title}
                />

                <Box sx={{ mt: 2 }}>
                    <YouTubeSearch
                        aiQuery={slide.media_search_query}
                        initialQuery={initialQuery}
                        selectedVideoId={selectedVideo?.videoId ?? null}
                        onSelectVideo={handleSelectVideo}
                        onQueryChange={handleQueryChange}
                        onInitialLoadComplete={handleInitialLoadComplete}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default YouTubeModal;
